/**
 * Authentication hook
 * Handles SSO authentication state and flow
 */

import { useState, useEffect, useCallback } from 'react';
import {
  authClient,
  handleAuthError,
  AUTH_ERROR_EVENT,
  clearSession,
  getStoredSession,
  isSafeReturnToPath,
  getStoredReturnToPath,
  clearStoredReturnToPath,
} from '@/lib/auth-client';
import { getDashboardUrl } from '@/components/theme-provider';
import { withRetry } from '@/lib/retry';
import type { AuthSession, SessionScope } from '@/types/auth';

// Module-level state to share SSO exchange across strict mode double-mounts
let ssoExchangePromise: Promise<AuthSession> | null = null;
let ssoExchangeInProgress = false; // Flag to indicate exchange is happening

// Escalation state for the auth-error listener (module-level on purpose: it is
// app-global and must survive strict-mode remounts).
//
// A 401/403 is "forgiven" once when the session cookie still validates
// server-side (transient blip). A second auth error while that forgiveness is
// still fresh means the cookie is valid but the backend keeps denying access —
// e.g. the account moved from master/agent-enabled to a client without an
// enabled WhatsApp line, or its agent access was revoked. Revalidating can
// never fix that, so we escalate: log out (clear the dead cookie) and send the
// user to the dashboard, instead of forgiving forever and leaving the UI stuck.
// Re-entering WhatsApp from the dashboard mints a fresh code with the account's
// current scope, so a merely scope-changed (still agent-enabled) session
// self-heals from there — and leaving the app makes a redirect loop impossible.
let handlingAuthError = false;
let forgivenAuthErrorAt = 0;
const AUTH_ERROR_ESCALATION_WINDOW_MS = 60_000;

/**
 * Whether two brand-access scopes grant the same thing. GET /sso/session
 * re-derives the scope from the grant on every call, so a mismatch against the
 * scope cached at login means the account was re-scoped server-side (e.g. the
 * dashboard user moved from a master to a child client) — grounds to escalate
 * on the FIRST auth error instead of forgiving it as transient.
 * locationName is cosmetic (a rename is not a re-scope) and excluded.
 */
function sameScope(a: SessionScope | null | undefined, b: SessionScope | null | undefined): boolean {
  if (!a || !b) return !a === !b;
  return (
    a.brandId === b.brandId &&
    a.locationId === b.locationId &&
    a.readOnly === b.readOnly &&
    a.hasOwnLine === b.hasOwnLine
  );
}

export interface UseAuthReturn {
  session: AuthSession | null;
  loading: boolean;
  /** True only after auth initialization is fully complete (including consume if needed) */
  ready: boolean;
  error: Error | null;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false); // Only true after full auth init (including consume if needed)
  const [error, setError] = useState<Error | null>(null);

  // Listen for auth errors from API (401/403) — retry session check before showing error
  useEffect(() => {
    let cancelled = false;

    const handleAuthErrorEvent = async () => {
      // A burst of parallel 401/403s (several polls failing in the same tick)
      // fires this once per failed request — handle the first, drop the rest.
      if (handlingAuthError) return;
      handlingAuthError = true;
      try {
        console.log('Auth error event received, retrying session check...');
        try {
          // Wait briefly and retry — the backend may be restarting (deploy)
          await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;
          // Snapshot the scope we were operating under BEFORE dropping the cache,
          // to compare against the freshly re-derived one below.
          const cached = getStoredSession();
          // Drop the local cache first: the server just rejected it, so revalidate
          // the cookie server-side instead of trusting the unexpired cached copy.
          clearSession();
          const retried = await authClient.getCurrentSession();
          if (cancelled) return;
          if (retried.authenticated) {
            // Structural verdicts from /sso/session make the errors non-transient —
            // skip the forgiveness round and escalate immediately:
            // - agent_access false: the backend says the identity has no operable line.
            // - scope changed: the account was re-scoped server-side.
            const accessRevoked = retried.agentAccess === false;
            const rescoped = cached?.authenticated ? !sameScope(cached.scope, retried.scope) : false;
            if (!accessRevoked && !rescoped && Date.now() - forgivenAuthErrorAt > AUTH_ERROR_ESCALATION_WINDOW_MS) {
              console.log('Session still valid after retry, ignoring transient error');
              forgivenAuthErrorAt = Date.now();
              setSession(retried);
              return;
            }
            // Either the scope changed server-side, or this is the second auth error
            // right after a "session still valid" verdict: the cookie is fine but the
            // backend keeps denying access (WhatsApp/agent access revoked or scope
            // changed). Clear the dead cookie and hand the user to the dashboard —
            // the surface their account can actually use.
            console.log(
              accessRevoked
                ? 'Backend reports no agent access — logging out, returning to dashboard'
                : rescoped
                  ? 'Brand access scope changed server-side — logging out, returning to dashboard'
                  : 'Session valid but access repeatedly denied — logging out, returning to dashboard'
            );
            await authClient.logout();
            if (cancelled) return;
            const dashboardUrl = getDashboardUrl();
            if (dashboardUrl) {
              window.location.href = dashboardUrl;
              return;
            }
            // No dashboard URL configured — fall back to the re-auth redirect.
            setError(null);
            setSession({ authenticated: false });
            return;
          }
        } catch {
          // Revalidation failed (backend unreachable) — fall through to re-auth;
          // the dashboard mints a fresh code and the consume path retries transient failures.
        }
        if (cancelled) return;
        // Session genuinely expired/invalid: redirect to re-auth via AuthGuard's
        // unauthenticated branch instead of showing the connection-error screen.
        console.log('Auth error confirmed after retry, clearing session and re-authenticating');
        handleAuthError();
        setError(null);
        setSession({ authenticated: false });
      } finally {
        handlingAuthError = false;
      }
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthErrorEvent);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthErrorEvent);
    };
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      try {
        // Cross-app logout: Dashboard redirects here with ?action=logout
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'logout') {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          await authClient.logout();
          const dashboardUrl = getDashboardUrl();
          if (dashboardUrl) {
            window.location.href = `${dashboardUrl}/login`;
          }
          return;
        }

        // Check if we have a delegation code in the URL fragment OR if exchange is in progress
        const hasCode = window.location.hash.includes('code=');
        // Optional deep link carried alongside the code (e.g. "/orders?order=<id>" from the
        // dashboard's payment → WhatsApp-order chip). Captured BEFORE the exchange strips the hash.
        const returnToRaw = hasCode
            ? new URLSearchParams(window.location.hash.slice(1)).get('return_to')
            : null;
        const hashReturnTo = isSafeReturnToPath(returnToRaw) ? returnToRaw : null;
        // Fall back to the path stored by AuthGuard before it redirected to the dashboard's
        // /oauth/authorize (peeked, not consumed — strict-mode's second mount finishes the
        // exchange via the shared promise and still needs it; consumed on use below).
        const returnTo = hashReturnTo
            ?? ((hasCode || ssoExchangeInProgress) ? getStoredReturnToPath() : null);

        if (hasCode || ssoExchangeInProgress) {
          // CODE DETECTED OR EXCHANGE IN PROGRESS
          // CRITICAL: Clear any old session immediately to prevent stale data usage
          if (!ssoExchangeInProgress) {
            console.log('New SSO code detected, clearing old session...');
            clearSession();
          }

          setLoading(true);
          setError(null);
          // Ensure session state is null during consume to prevent any stale render
          setSession(null);

          // If SSO exchange is already in progress (strict mode double-mount), reuse the promise
          if (ssoExchangePromise) {
            console.log('SSO exchange already in progress, awaiting result...');
            try {
              const newSession = await ssoExchangePromise;
              if (mounted) {
                setSession(newSession);
                setError(null);
                console.log('SSO authentication successful (from shared promise)');
                if (returnTo) {
                  clearStoredReturnToPath();
                  window.location.replace(returnTo);
                }
              }
            } catch (err) {
              console.error('SSO exchange failed (shared):', err);
              if (mounted) {
                setError(err instanceof Error ? err : new Error('Authentication failed'));
                setSession({ authenticated: false });
              }
            } finally {
              if (mounted) {
                setLoading(false);
                setReady(true);
              }
            }
          } else {
            // Start new SSO exchange
            console.log('SSO code detected, exchanging for access token...');
            ssoExchangeInProgress = true;
            ssoExchangePromise = authClient.consumeSSOCode();

            try {
              const newSession = await ssoExchangePromise;
              if (mounted) {
                setSession(newSession);
                setError(null);
                console.log('SSO authentication successful');
                if (returnTo) {
                  clearStoredReturnToPath();
                  window.location.replace(returnTo);
                }
              }
            } catch (err) {
              // On error: show auth error screen
              console.error('SSO exchange failed:', err);
              if (mounted) {
                setError(err instanceof Error ? err : new Error('Authentication failed'));
                setSession({ authenticated: false });
              }
            } finally {
              if (mounted) {
                setLoading(false);
                setReady(true);
              }
              // Clear the exchange state after a short delay to allow strict mode second mount to use it
              setTimeout(() => {
                ssoExchangePromise = null;
                ssoExchangeInProgress = false;
              }, 100);
            }
          }
        } else {
          // NO CODE AND NO EXCHANGE IN PROGRESS: Check for existing session.
          // getCurrentSession THROWS when the backend is unreachable (network / 5xx),
          // so retry with backoff (~26s) to ride out a deploy window instead of
          // treating the outage as "logged out" and burning a delegation code.
          const currentSession = await withRetry(() => authClient.getCurrentSession(), {
            maxAttempts: 6,
            baseDelayMs: 1500,
            maxDelayMs: 8000,
          });
          if (mounted) {
            setSession(currentSession);
            setLoading(false);
            setReady(true); // No consume needed, ready immediately

            if (currentSession.authenticated) {
              console.log('Active session restored');
            } else {
              console.log('No active session found');
            }
          }
        }
      } catch (err) {
        // Unexpected error
        console.error('Authentication initialization error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Authentication failed'));
          setSession({ authenticated: false });
          setLoading(false);
          setReady(true); // Even on error, we're "ready" (just in error state)
        }
      }
    }

    initAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setError(null);
      await authClient.logout();
      setSession({ authenticated: false });
      console.log('Logged out successfully');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err : new Error('Logout failed'));
      throw err;
    }
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      const currentSession = await authClient.getCurrentSession();
      setSession(currentSession);

      if (!currentSession.authenticated) {
        console.log('Session expired or invalid');
      }
    } catch (err) {
      console.error('Session refresh error:', err);
      setError(err instanceof Error ? err : new Error('Session refresh failed'));
      setSession({ authenticated: false });
    }
  }, []);

  return {
    session,
    loading,
    ready,
    error,
    logout,
    refreshSession,
  };
}
